import React, { useState, useEffect, useRef } from 'react';
import BaseModal from '../BaseModal';
import { mdiEmailEditOutline } from '@mdi/js';
import { organizationApi } from '../../api/organization';
import { toast } from 'react-toastify';
import EmailTemplateEditor from './EmailTemplateEditor';
import { tokensToHandlebars } from '../../utils/emailTokens';
import './EmailTemplateEditorModal.css';

const EmailTemplateEditorModal = ({ isOpen, emailType, emailLabel, isCustom, onClose, onSaved }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [subject, setSubject] = useState('');
    // Initial body HTML loaded from the API. The editor seeds itself on init
    // and then manages content internally via its imperative ref. We don't
    // mirror every keystroke into React state — we read the editor's content
    // at save time via editorRef.current.getHandlebars().
    const [initialBody, setInitialBody] = useState('');
    const editorRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !emailType) return;
        setIsLoading(true);
        setShowResetConfirm(false);
        setSubject('');
        setInitialBody('');
        organizationApi.getEmailTemplateRaw(emailType)
            .then(res => {
                if (res.ok && res.data) {
                    setSubject(res.data.subject || '');
                    setInitialBody(res.data.bodyHtml || '');
                }
            })
            .catch(() => toast.error('Failed to load template.'))
            .finally(() => setIsLoading(false));
    }, [isOpen, emailType]);

    const handleSave = async () => {
        const handlebarsBody = editorRef.current ? editorRef.current.getHandlebars() : '';
        if (!subject.trim() || !handlebarsBody.trim()) {
            toast.error('Subject and body are required.');
            return;
        }
        // Subject may also contain chip spans if a future iteration adds chip
        // support there — convert defensively. Currently the subject is a
        // plain <input> so it has no chips, but tokensToHandlebars is a no-op
        // when there are no chip spans, so this is safe and future-proof.
        const handlebarsSubject = tokensToHandlebars(subject);
        setIsSaving(true);
        try {
            const res = await organizationApi.saveEmailTemplate(emailType, handlebarsSubject, handlebarsBody);
            if (res.ok) {
                toast.success('Template saved.');
                onSaved?.();
                onClose();
            } else {
                toast.error(res.data?.message || 'Failed to save template.');
            }
        } catch {
            toast.error('Failed to save template.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        setIsResetting(true);
        try {
            const res = await organizationApi.resetEmailTemplate(emailType);
            if (res.ok) {
                toast.success('Template reset to system default.');
                onSaved?.();
                onClose();
            } else {
                toast.error(res.data?.message || 'Failed to reset template.');
            }
        } catch {
            toast.error('Failed to reset template.');
        } finally {
            setIsResetting(false);
            setShowResetConfirm(false);
        }
    };

    // Reset confirmation toggles the BaseModal's body content rather than
    // nesting a ConfirmationModal — see web CLAUDE.md "ConfirmationModal
    // Inside BaseModal: Portal Trap" for why.
    const footer = showResetConfirm ? (
        <>
            <button
                className="base-modal-cancel-btn"
                onClick={() => setShowResetConfirm(false)}
                disabled={isResetting}
            >
                Go Back
            </button>
            <button
                className="base-modal-save-btn email-template-editor-modal__confirm-reset-btn"
                onClick={handleReset}
                disabled={isResetting}
            >
                {isResetting ? 'Resetting...' : 'Yes, Reset to Default'}
            </button>
        </>
    ) : (
        <>
            <button
                className="base-modal-cancel-btn email-template-editor-modal__reset-btn"
                onClick={() => setShowResetConfirm(true)}
                disabled={isSaving || isLoading}
            >
                Reset to Default
            </button>
            <button className="base-modal-cancel-btn" onClick={onClose} disabled={isSaving}>
                Cancel
            </button>
            <button
                className="base-modal-save-btn"
                onClick={handleSave}
                disabled={isSaving || isLoading}
            >
                {isSaving ? 'Saving...' : 'Save Template'}
            </button>
        </>
    );

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title={emailLabel ? `Edit Template: ${emailLabel}` : 'Edit Email Template'}
            icon={mdiEmailEditOutline}
            maxWidth={760}
            footer={footer}
        >
            {isLoading ? (
                <p className="email-template-editor-modal__loading">Loading template…</p>
            ) : showResetConfirm ? (
                <div className="email-template-editor-modal__reset-confirm">
                    <p className="email-template-editor-modal__reset-confirm-message">
                        This will remove your custom template and restore the system default. This cannot be undone.
                    </p>
                </div>
            ) : (
                <div className="email-template-editor-modal__content">
                    {isCustom && (
                        <div className="email-template-editor-modal__custom-notice">
                            <span className="email-template-editor-modal__custom-badge">Custom</span>
                            This template has been customised from the system default.
                        </div>
                    )}
                    <div className="email-template-editor-modal__field">
                        <label className="email-template-editor-modal__label">Subject</label>
                        <p className="form-hint">The subject line sent to recipients.</p>
                        <input
                            className="form-input"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            placeholder="Email subject line"
                        />
                    </div>
                    <div className="email-template-editor-modal__field">
                        <label className="email-template-editor-modal__label">Email Body</label>
                        <p className="form-hint">Edit the email body. Click "+ Insert Field" to add a token chip at the cursor.</p>
                        {/* The editor seeds itself from initialBody on first init.
                            Re-keying on emailType ensures a fresh editor when the
                            modal opens for a different template type. */}
                        <EmailTemplateEditor
                            key={emailType}
                            ref={editorRef}
                            emailType={emailType}
                            initialHtml={initialBody}
                            height={320}
                        />
                    </div>
                    <p className="email-template-editor-modal__footer-note">
                        Note: The "via Dance Locale" footer and button colours are system-controlled and cannot be changed here.
                    </p>
                </div>
            )}
        </BaseModal>
    );
};

export default EmailTemplateEditorModal;
